import type { ToolCallLink } from "@demo/server/api/contracts/toolCall";

/**
 * Favicons are not on the tool-call wire. Until the producer sends `link.favicon`, the client resolves one
 * by host from Google's public icon service, asking for `FAVICON_SIZE` px. For a host it has no icon for, it
 * answers 404 but still sends a small placeholder image, which the browser shows without an error; that
 * placeholder is 16px, so an icon that loads smaller than `FAVICON_MIN_SIZE` counts as missing.
 */
const FAVICON_SERVICE_URL = "https://www.google.com/s2/favicons";
const FAVICON_SIZE = 64;
export const FAVICON_MIN_SIZE = 32;

/** A producer `domain` can be a site name ("Facebook") rather than a host; only a host resolves an icon. */
const looksLikeHost = (value: string): boolean => /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(value);

/** The host to look an icon up by: the link's `domain` when it is a host, else the hostname of `url`. */
export function getIconHost(host: string | undefined, url: string | undefined): string | undefined {
  if (host && looksLikeHost(host)) return host;
  if (!url) return undefined;
  try {
    return new URL(url).hostname || undefined;
  } catch {
    return undefined;
  }
}

/** The host a result row shows: the producer's `domain`, else the hostname of `url`. */
export function getLinkHost(link: Pick<ToolCallLink, "url" | "domain">): string | undefined {
  if (link.domain) return link.domain;
  if (!link.url) return undefined;
  try {
    return new URL(link.url).hostname;
  } catch {
    return undefined;
  }
}

export function getFaviconUrl(host: string): string {
  return `${FAVICON_SERVICE_URL}?domain=${encodeURIComponent(host)}&sz=${FAVICON_SIZE}`;
}

/** The letter a site's fallback tile shows: the first letter of its name, or of its host without `www.`. */
export function getSiteInitial(name: string | undefined, host: string | undefined): string | undefined {
  const source = (name && !looksLikeHost(name) ? name : host?.replace(/^www\./i, "")) ?? name;
  const letter = source?.trim().match(/\p{L}|\p{N}/u)?.[0];
  return letter?.toLocaleUpperCase();
}

/** Muted tile colours; a site always gets the same one, picked from its host. */
const TILE_HUES = [210, 250, 290, 330, 10, 30, 150, 180];
export function getSiteTileColor(key: string): string {
  let hash = 0;
  for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return `hsl(${TILE_HUES[Math.abs(hash) % TILE_HUES.length]} 35% 32%)`;
}
